import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SEO } from '../components/SEO';

const PARTNERS = [
  ['Fashionphile', 'Premium authenticated luxury accessories and jewelry'],
  ['The RealReal', 'Consignment luxury with wide selection'],
  ['1stDibs', 'Rare vintage and antique pieces from vetted dealers'],
  ['Vestiaire Collective', 'Community-driven European luxury'],
];

export const AffiliateDisclosurePage = () => {
  return (
    <div data-testid="affiliate-disclosure-page" className="bg-[#FAF9F6]">
      <SEO
        title="Affiliate Disclosure — Transparency & Trust"
        description="How Jewelry Refined earns revenue through affiliate partnerships with luxury resale platforms. Full transparency about our editorial independence and review process."
        url="/affiliate-disclosure"
      />

      {/* Editorial Hero */}
      <section className="border-b border-[#E5E0D8]">
        <div className="container-editorial py-20 lg:py-28">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#888] mb-12">
            <Link to="/" className="hover:text-[#111] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-[#111]">Affiliate Disclosure</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-9">
              <p className="overline overline-gold mb-6 animate-fade-in">Editorial Standards</p>
              <h1 className="heading-editorial heading-editorial-lg text-[#111] mb-8 animate-fade-in-up">
                Affiliate <span className="italic">Disclosure</span>
              </h1>
              <p className="text-[#555] text-xl lg:text-2xl leading-relaxed font-light max-w-3xl">
                Transparency is a foundation of trust. Here's exactly how Jewelry Refined earns
                revenue and how affiliate relationships shape — and don't shape — our editorial.
              </p>
            </div>
            <div className="lg:col-span-3 lg:border-l border-[#E5E0D8] lg:pl-8">
              <p className="overline mb-3">Last Updated</p>
              <p className="font-heading text-2xl text-[#111]">February 2026</p>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="container-editorial py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-8 lg:col-start-2">
            <div className="prose-editorial">
              <h2>How We Earn Revenue</h2>
              <p>
                Jewelry Refined is a participant in affiliate programs with luxury resale
                platforms including Fashionphile, The RealReal, 1stDibs, and Vestiaire
                Collective. When you click a link from our site to one of these platforms and
                make a purchase, we may earn a commission at no additional cost to you.
              </p>
              <p>
                This affiliate revenue allows us to keep every guide, designer profile, and
                authentication walkthrough free for readers — and to invest in deeper editorial
                research over time.
              </p>

              <h2>Our Commitment to Honest Recommendations</h2>
              <p>
                While we earn commissions from our partner platforms, our recommendations are
                based solely on quality, trustworthiness, and value. We only partner with
                platforms we'd personally use and recommend to friends and family.
              </p>
              <p>
                Editorial content is created independently and is not influenced by affiliate
                partnerships. If a platform doesn't meet our standards, we won't recommend it —
                regardless of commission potential.
              </p>

              <h2>How Affiliate Links Work</h2>
              <p>
                Throughout our site, you'll find links marked with phrases like <em>"Shop on Fashionphile"</em> or <em>"Visit The RealReal"</em>. These are affiliate links. When you:
              </p>
              <ul>
                <li>Click an affiliate link from our site</li>
                <li>Are directed to the partner's website</li>
                <li>Make a purchase within the platform's referral window (typically 24–30 days)</li>
              </ul>
              <p>
                We receive a small commission. The price you pay is exactly the same as if
                you'd visited the partner's site directly.
              </p>

              <blockquote>
                Our recommendations are based on quality and trustworthiness — never on
                commission rates. If we wouldn't buy from a platform ourselves, we won't ask
                you to.
              </blockquote>

              <h2>Your Privacy</h2>
              <p>
                Affiliate links may use cookies to track referrals. These cookies are managed
                by our partner platforms and governed by their privacy policies. We do not
                receive personal information about your purchases beyond aggregate reporting on
                commission earnings.
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-3 lg:col-start-10">
            <div className="sticky top-32 space-y-12">
              <div>
                <p className="overline overline-gold mb-6">On This Page</p>
                <ul className="space-y-3 border-l border-[#E5E0D8] pl-4">
                  {[
                    'How We Earn Revenue',
                    'Honest Recommendations',
                    'How Affiliate Links Work',
                    'Your Privacy',
                    'Our Partners',
                  ].map((label) => (
                    <li key={label} className="text-sm text-[#555] hover:text-[#111] cursor-default">
                      {label}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#111] text-white p-8">
                <p className="overline text-[#C69C6D] mb-4">Have A Question?</p>
                <p className="text-white/70 text-sm leading-relaxed mb-6">
                  We're happy to clarify any part of our affiliate relationships or editorial
                  process.
                </p>
                <Link
                  to="/contact"
                  className="text-xs tracking-[0.15em] uppercase border-b border-[#C69C6D] text-[#C69C6D] hover:text-white hover:border-white pb-1 inline-flex items-center gap-2 transition-colors"
                >
                  Contact Editors
                  <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Our Partners — Editorial Cards */}
      <section className="section-editorial border-t border-[#E5E0D8]">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
            <div>
              <p className="overline overline-gold mb-3">The Partners</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl">Who We Work With</h2>
            </div>
            <Link
              to="/partners"
              className="hidden md:inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase border-b border-[#111] pb-1 hover:text-[#C69C6D] hover:border-[#C69C6D] transition-colors"
            >
              All Partners
              <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E0D8]">
            {PARTNERS.map(([name, desc]) => (
              <div key={name} className="bg-[#FAF9F6] p-8 lg:p-10">
                <p className="overline overline-gold mb-4">Affiliate Partner</p>
                <h3 className="font-heading text-2xl text-[#111] mb-3">{name}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-[#111] text-white py-20 lg:py-28">
        <div className="container-editorial text-center max-w-2xl mx-auto">
          <p className="overline text-[#C69C6D] mb-6">Still Reading?</p>
          <h2 className="heading-editorial text-3xl lg:text-5xl mb-6">
            Thank you for your <span className="italic">trust</span>
          </h2>
          <p className="text-white/60 text-lg leading-relaxed mb-10">
            Transparent affiliate relationships are how independent editorial sites survive.
            Every recommendation we publish is one we'd stake our names on.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/about" className="btn-editorial border-white text-white hover:bg-white hover:text-[#111]">
              About Our Team
            </Link>
            <Link to="/contact" className="btn-editorial border-[#C69C6D] text-[#C69C6D] hover:bg-[#C69C6D] hover:text-white">
              Get In Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AffiliateDisclosurePage;
